import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Search, Check, Building2, Car } from "lucide-react";
import { api } from "@/lib/api";
import { Logo, SiteTag } from "@/components/Logo";
import { useSiteInfo } from "@/hooks/useSiteInfo";

const slotLabel = (s) =>
    s === "morning" ? "Voormiddag · 08:00 – 12:00" : "Namiddag · 13:00 – 17:00";

const formatDateNL = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("nl-BE", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

export default function Lookup() {
    const site = useSiteInfo();
    const [mode, setMode] = useState("plate"); // "plate" | "vat"
    const [plate, setPlate] = useState("");
    const [vat, setVat] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState("");

    const canSubmit =
        !loading &&
        ((mode === "plate" && plate.trim().length >= 4) ||
            (mode === "vat" && vat.trim().length >= 4));

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setLoading(true);
        setError("");
        setResults(null);
        try {
            const payload =
                mode === "plate"
                    ? { license_plate: plate.trim() }
                    : { vat_number: vat.trim() };
            const res = await api.post("/bookings/lookup", payload);
            setResults(res.data);
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Kon geen boeking ophalen. Probeer opnieuw.",
            );
        } finally {
            setLoading(false);
        }
    };

    // Group results by date for the company (VAT) view.
    const grouped = useMemo(() => {
        if (!results) return [];
        const map = new Map();
        for (const b of results) {
            if (!map.has(b.date)) map.set(b.date, []);
            map.get(b.date).push(b);
        }
        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, bookings]) => ({ date, bookings }));
    }, [results]);

    return (
        <div className="min-h-screen flex flex-col">
            <header className="flex items-center justify-between px-6 md:px-12 lg:px-16 py-6 border-b border-[#e5e5e5] gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                    <Logo />
                    <SiteTag name={site.name} city={site.city} />
                </div>
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-[#555] hover:text-[#0A1F44] transition-colors"
                    data-testid="lookup-back-home"
                >
                    <ArrowLeft size={14} /> Home
                </Link>
            </header>

            <main className="flex-1 px-6 md:px-12 lg:px-16 py-16 md:py-24">
                <div className="max-w-3xl">
                    <span className="text-sm tracking-widest text-[#ED6A14] uppercase font-semibold">
                        Zelfbediening
                    </span>
                    <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter mt-4 text-[#0A1F44]">
                        Vind mijn boeking
                    </h1>
                    <p className="text-[#555] mt-4 max-w-md">
                        Zoek op nummerplaat (1 wagen) of op btw-nummer (alle
                        wagens van je bedrijf) — geen datum nodig.
                    </p>

                    {/* Mode tabs */}
                    <div
                        className="mt-10 inline-flex border-2 border-[#e5e5e5]"
                        role="tablist"
                        data-testid="lookup-mode-tabs"
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={mode === "plate"}
                            onClick={() => {
                                setMode("plate");
                                setResults(null);
                                setError("");
                            }}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors ${
                                mode === "plate"
                                    ? "bg-[#0A1F44] text-white"
                                    : "text-[#555] hover:text-[#0A1F44]"
                            }`}
                            data-testid="lookup-mode-plate"
                        >
                            <Car size={16} /> Nummerplaat
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={mode === "vat"}
                            onClick={() => {
                                setMode("vat");
                                setResults(null);
                                setError("");
                            }}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors ${
                                mode === "vat"
                                    ? "bg-[#0A1F44] text-white"
                                    : "text-[#555] hover:text-[#0A1F44]"
                            }`}
                            data-testid="lookup-mode-vat"
                        >
                            <Building2 size={16} /> Btw-nummer
                        </button>
                    </div>

                    <form
                        onSubmit={submit}
                        className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-xl"
                        data-testid="lookup-form"
                    >
                        {mode === "plate" ? (
                            <div className="sm:col-span-2">
                                <label className="text-xs uppercase tracking-widest text-[#555]">
                                    Nummerplaat
                                </label>
                                <input
                                    value={plate}
                                    onChange={(e) =>
                                        setPlate(e.target.value.toUpperCase())
                                    }
                                    placeholder="1-ABC-234"
                                    className="w-full border-b-2 border-[#e5e5e5] bg-transparent py-3 text-xl font-display tracking-wider focus:outline-none focus:border-[#ED6A14] transition-colors uppercase text-[#0A1F44]"
                                    data-testid="lookup-plate-input"
                                />
                            </div>
                        ) : (
                            <div className="sm:col-span-2">
                                <label className="text-xs uppercase tracking-widest text-[#555]">
                                    Btw-nummer
                                </label>
                                <input
                                    value={vat}
                                    onChange={(e) => setVat(e.target.value.toUpperCase())}
                                    placeholder="BE0123.456.789"
                                    className="w-full border-b-2 border-[#e5e5e5] bg-transparent py-3 text-xl font-display tracking-wider focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                                    data-testid="lookup-vat-input"
                                />
                                <p className="text-xs text-[#555] mt-2">
                                    Toont alle geplande wagens onder dit
                                    btw-nummer, gegroepeerd per dag.
                                </p>
                            </div>
                        )}
                        <div className="sm:col-span-2">
                            <button
                                type="submit"
                                disabled={!canSubmit}
                                className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-7 py-3.5 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                                data-testid="lookup-submit-btn"
                            >
                                {loading ? (
                                    <Loader2 className="animate-spin" size={16} />
                                ) : (
                                    <Search size={16} />
                                )}{" "}
                                Zoek mijn boeking
                                {mode === "vat" ? "en" : ""}
                            </button>
                        </div>
                    </form>

                    {error && (
                        <p
                            className="mt-8 text-sm text-[#D92D20]"
                            data-testid="lookup-error"
                        >
                            {error}
                        </p>
                    )}

                    {results && (
                        <div className="mt-12" data-testid="lookup-results">
                            {results.length === 0 ? (
                                <div
                                    className="border border-[#e5e5e5] p-6 text-[#555]"
                                    data-testid="lookup-empty"
                                >
                                    Geen geplande wasbeurt gevonden
                                    {mode === "plate"
                                        ? " met deze nummerplaat."
                                        : " onder dit btw-nummer."}
                                    <div className="mt-4">
                                        <Link
                                            to="/book"
                                            className="inline-flex items-center gap-2 text-[#ED6A14] hover:text-[#C9550C] font-semibold"
                                        >
                                            Boek een wasbeurt{" "}
                                            <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p
                                        className="text-sm text-[#555] mb-6"
                                        data-testid="lookup-result-summary"
                                    >
                                        <strong>{results.length}</strong>{" "}
                                        geplande wasbeurt
                                        {results.length === 1 ? "" : "en"}{" "}
                                        gevonden
                                        {mode === "vat"
                                            ? `, verspreid over ${grouped.length} dag${grouped.length === 1 ? "" : "en"}`
                                            : ""}
                                        .
                                    </p>
                                    <div className="space-y-10">
                                        {grouped.map((day) => (
                                            <DayGroup
                                                key={day.date}
                                                day={day}
                                                showHeader={mode === "vat" || day.bookings.length > 1}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

function DayGroup({ day, showHeader }) {
    return (
        <div data-testid={`lookup-day-${day.date}`}>
            {showHeader && (
                <h2 className="font-display font-bold text-xl text-[#0A1F44] mb-4">
                    {formatDateNL(day.date)}
                    <span className="ml-3 text-xs font-mono-ui text-[#555] font-normal uppercase tracking-widest">
                        {day.bookings.length} wagen
                        {day.bookings.length === 1 ? "" : "s"}
                    </span>
                </h2>
            )}
            <div className="space-y-4">
                {day.bookings.map((b) => (
                    <BookingCard key={b.id} booking={b} />
                ))}
            </div>
        </div>
    );
}

function BookingCard({ booking }) {
    const isConfirmed = booking.status === "confirmed";
    return (
        <div
            className={`border p-6 ${isConfirmed ? "border-[#ED6A14]" : "border-[#e5e5e5]"}`}
            data-testid={`lookup-booking-${booking.id}`}
        >
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <span
                    className={`inline-flex items-center gap-2 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${
                        isConfirmed
                            ? "bg-[#ED6A14]/10 text-[#ED6A14]"
                            : "bg-[#eaeaea] text-[#0A1F44]"
                    }`}
                >
                    {isConfirmed ? <Check size={12} /> : null}
                    {isConfirmed ? "Bevestigd" : "In behandeling"}
                </span>
                <span className="font-mono-ui text-xs text-[#555]">
                    {booking.id.slice(0, 8)}
                </span>
            </div>
            <h3 className="font-display font-bold text-2xl tracking-tight text-[#0A1F44]">
                {booking.service_name}
            </h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <Row k="Datum" v={formatDateNL(booking.date)} />
                <Row k="Tijdslot" v={slotLabel(booking.slot)} />
                <Row k="Nummerplaat" v={booking.license_plate} />
                <Row
                    k="Bedrag (factuur volgt)"
                    v={`€${booking.amount_eur.toFixed(2).replace(".", ",")}`}
                />
            </div>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div>
            <p className="text-xs uppercase tracking-widest text-[#555]">
                {k}
            </p>
            <p className="text-[#0A1F44] font-medium mt-1">{v}</p>
        </div>
    );
}
